import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Polaroid } from '../components/ui/Polaroid';
import { InkUnderline, RevealText } from '../components/ui/RevealText';
import { MarketingNav, BackToTop } from '../components/ui/MarketingNav';
import { SiteFooter } from './AboutPage';

export default function PricingPage() {
  return (
    <div className="min-h-screen">
      <MarketingNav />

      {/* ─── Hero ─── */}
      <section className="container-page relative pb-3xl pt-lg">
        <div className="mx-auto max-w-4xl text-center">
          <p className="kicker justify-center">Prețuri</p>

          <RevealText
            as="h1"
            className="mt-md font-serif leading-[0.95]"
            delay={0.1}
            stagger={0.04}
          >
            {`Un preț. `}
          </RevealText>
          <h1
            className="font-serif leading-[0.95]"
            style={{
              fontSize: 'clamp(3rem, 6vw, 5.4rem)',
              fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1",
            }}
          >
            <span className="text-gradient italic">Fără surprize.</span>
          </h1>

          <p className="mt-lg max-w-2xl mx-auto text-lg text-fg-soft">
            Plătești per eveniment, nu per invitație, nu per poză, nu per
            lună. <InkUnderline soft>749 de lei, tot inclus</InkUnderline>.
            Și include un PDF de revistă printabil, gata dus la tipografie.
          </p>
        </div>

        <div className="relative mx-auto mt-3xl grid max-w-3xl grid-cols-1 items-center gap-xl lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <Polaroid
              src="https://images.unsplash.com/photo-1606800052052-a08af7148866?w=900&q=80"
              alt="Imagine editorială pentru prețuri"
              caption="✦ un singur plan, tot inclus"
              rotate={-1.5}
              aspect="3/2"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <p
              className="font-serif text-accent"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 0.9, fontVariationSettings: "'opsz' 144" }}
            >
              749
            </p>
            <p className="mt-xs font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
              lei · per eveniment · TVA inclus
            </p>
            <p className="mt-md text-fg-soft">
              Un singur plan, fără tier-uri, fără „Pro", fără
              „Enterprise". Pentru nunți, botezuri, majorate, lansări și
              orice eveniment unde vrei ca oamenii să-și amintească.
            </p>
            <Link to="/contact" className="btn-primary mt-lg inline-flex">
              Începe acum
            </Link>
            <p className="mt-sm font-mono text-[0.7rem] uppercase tracking-[0.18em] text-fg-soft">
              Plătești doar când e gata evenimentul
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── What's included ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page paper-grain relative">
          <div className="mx-auto max-w-2xl text-center">
            <p className="kicker justify-center">Ce intră</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Tot ce-ți trebuie pentru o seară,
              <br />
              <em className="italic text-fg-soft">în 749 de lei.</em>
            </h2>
          </div>

          <div className="mx-auto mt-3xl grid max-w-4xl grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDED.map(([title, body], i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ delay: i * 0.04, duration: 0.5 }}
                className="border-t border-border pt-md"
              >
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
                  ✓
                </p>
                <p className="mt-sm font-serif text-lg">{title}</p>
                <p className="mt-xs text-sm text-fg-soft">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Extras (opționale) ─── */}
      <section className="container-page py-4xl">
        <div className="grid grid-cols-1 gap-2xl lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="kicker">Opționale</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Dacă vrei mai mult,
              <br />
              <em className="italic text-fg-soft">plătești doar atunci.</em>
            </h2>
            <p className="mt-md text-fg-soft">
              Nimic din ce-i mai jos nu e obligatoriu. Le-am adăugat pentru
              că ni le-ați cerut, nu pentru că am vrut să umflăm prețul.
            </p>
          </div>

          <div className="lg:col-span-7">
            <ul className="divide-y divide-border">
              {EXTRAS.map(([t, p, b]) => (
                <li key={t} className="grid grid-cols-[1fr_auto] items-baseline gap-md py-md">
                  <div>
                    <p className="font-serif text-lg">{t}</p>
                    <p className="mt-xs text-sm text-fg-soft">{b}</p>
                  </div>
                  <p
                    className="font-serif text-right"
                    style={{ fontSize: '1.4rem', fontVariationSettings: "'opsz' 144" }}
                  >
                    {p}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ─── Comparison: noi vs. alternatives ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <p className="kicker justify-center">Comparație sinceră</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Cât costă <em className="italic text-fg-soft">altfel</em>?
            </h2>
          </div>

          <div className="mx-auto mt-3xl max-w-4xl overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-md pr-md font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
                    —
                  </th>
                  <th className="py-md pr-md font-serif text-lg">GuestLens</th>
                  <th className="py-md pr-md font-serif text-lg text-fg-soft">Fotograf clasic</th>
                  <th className="py-md pr-md font-serif text-lg text-fg-soft">Google Drive / iCloud</th>
                </tr>
              </thead>
              <tbody className="font-serif text-sm">
                {[
                  ['Toată lumea contribuie cu telefonul', '✓', '✕', '✓'],
                  ['Vedere de la toate mesele, nu doar din față', '✓', '✕', '✓'],
                  ['Moderare blândă (tu decizi)', '✓', '✕', '✕'],
                  ['Aspect revistă, printabil', '✓', 'uneori', '✕'],
                  ['Fără aplicație, fără cont', '✓', '—', '✕'],
                  ['Cost per eveniment', '749 lei', '2.500-8.000 lei', '0 (dar 30+ GB de poze într-un folder)'],
                  ['Cost per invitație', '0', '—', '0'],
                  ['Stres la final', '0', 'ridicat', 'ridicat'],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-border/50">
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={`py-sm pr-md ${
                          j === 1 ? 'text-accent font-medium' : 'text-fg-soft'
                        } ${j === 0 ? 'text-fg' : ''}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── Pull quote ─── */}
      <section className="border-y border-border bg-bg py-3xl">
        <div className="container-page">
          <blockquote className="mx-auto max-w-3xl text-center">
            <p
              className="font-serif italic"
              style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', lineHeight: 1.3 }}
            >
              „Am plătit 749 de lei și am primit 1.842 de cadre.
              Am plătit 4.500 de lei pe fotograf și am primit 380.
              Ghici care e pe peretele din bucătărie."
            </p>
            <p className="mt-md font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
              — Mireasa, nuntă cu 180 de invitați, iulie 2026
            </p>
          </blockquote>
        </div>
      </section>

      {/* ─── For each event type ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Pentru orice fel de seară</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Același preț,{' '}
            <em className="italic text-fg-soft">orice context</em>.
          </h2>
        </div>

        <div className="mt-3xl grid grid-cols-1 gap-2xl md:grid-cols-2 lg:grid-cols-4">
          {/* Disclaimer: pozele din secțiune sunt demonstrative */}
          <p className="col-span-full -mt-xl mb-sm font-mono text-[0.7rem] uppercase tracking-[0.18em] text-fg-soft/70 lg:order-first">
            ✦ Pozele de mai jos sunt demonstrative (Unsplash), nu din evenimente reale.
          </p>
          {EVENT_TYPES.map((e, i) => (
            <motion.div
              key={e.tag}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
            >
              <Polaroid
                src={e.img}
                alt={e.tag}
                tag={e.tag}
                rotate={e.rotate}
                aspect="4/5"
                delay={i * 80}
              />
              <h3
                className="mt-md font-serif"
                style={{ fontSize: '1.4rem', fontVariationSettings: "'opsz' 144" }}
              >
                {e.title}
              </h3>
              <p className="mt-xs text-sm text-fg-soft">{e.blurb}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="container-page pb-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Gata de citit destul</p>
          <h2 className="mt-sm font-serif" style={{ fontVariationSettings: "'opsz' 144" }}>
            Încearcă <em className="italic text-fg-soft">fără să plătești</em>.
          </h2>
          <p className="mt-md text-fg-soft">
            Deschizi o galerie demo, vezi cum arată, decizi după.
            Fără card, fără „trial de 14 zile", fără mail-uri urâte.
          </p>
          <div className="mt-xl flex flex-wrap justify-center gap-sm">
            <a href="/#demo-live" className="btn-primary">
              Deschide galeria demo
            </a>
            <Link to="/cum-functioneaza" className="btn-ghost">
              Cum funcționează →
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}

const INCLUDED = [
  ['Poze nelimitate', 'De la toți invitații, fără plafon. 50 sau 500, același preț.'],
  ['3 moduri de vizualizare', 'Revistă, Grilă, Noutăți. Fiecare cu farmecul lui.'],
  ['Moderare blândă', 'Tu decizi ce apare public, în ritmul tău.'],
  ['Comentarii și like-uri', 'Reacții de la invitați, pe loc, pe fiecare cadru.'],
  ['Stocare 1 an inclusă', 'Fără griji, fără costuri ascunse, fără „premium storage".'],
  ['Link personalizat', 'Ușor de trimis și de reținut.'],
  ['QR-uri printabile per masă', 'PDF gata de print, A4, câte unul per masă.'],
  ['Export complet la final', 'ZIP cu toate pozele în original, fără watermark.'],
  ['Suport pe mail, în aceeași zi', 'guestlens.events@gmail.com · răspundem noi, nu un bot.'],
];

const EXTRAS = [
  ['PDF de revistă printabil', '80 lei', 'Format A4, copertă personalizată, gata dus la tipografie.'],
  ['Prelungire stocare 1 an', '49 lei', 'După primul an inclus, păstrăm pozele încă 12 luni.'],
  ['Moderator delegat', '0 lei', 'Dai acces unui prieten de încredere, fără costuri suplimentare.'],
  ['Custom branding (corporate)', 'de la 1.500 lei', 'Logo, culori, domeniu propriu. Doar pentru evenimente corporate.'],
];

const EVENT_TYPES = [
  {
    tag: 'Nuntă',
    title: 'Ziua în care toți au telefonul în mână.',
    img: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=700&q=80',
    blurb: '200 de invitați, 12 ore, ~2.000 de cadre. Galeria se deschide duminică la cafea.',
    rotate: -2,
  },
  {
    tag: 'Botez',
    title: 'Familie mică, galerie mare.',
    img: 'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=700&q=80',
    blurb: '50 de oameni, dar nașii, bunicii, verii — toți cu telefonul pregătit.',
    rotate: 2,
  },
  {
    tag: 'Majorat',
    title: 'Prietenii tăi, așa cum îi știi.',
    img: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=700&q=80',
    blurb: '130 de prieteni, o noapte, cadre pe care niciun fotograf nu le-ar fi prins.',
    rotate: 1.5,
  },
  {
    tag: 'Corporate',
    title: 'Brand launch, gala, conferință.',
    img: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=700&q=80',
    blurb: 'Brief scurt, galerie gata în 24h. Cu branding, dacă vrei.',
    rotate: -1.5,
  },
];
